import React, { useState, useRef, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import {Link, useNavigate, useParams} from "react-router-dom";
import styled from "styled-components";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { updateAboutUsStepCards, getAboutUsStepCards, createAboutUsStepCards, } from "../../../api/axios";
import CmsCircularProgress from "../../Common/CircularProgress";
import removeIcon from "../../../Assets/remove-image-photo.svg";
import { showToast } from "../../../Utills/toast";
import FormGroup from "../../Common/FormGroup";
import { Formik } from 'formik';
import * as Yup from 'yup';

const validationSchema = (isEdit) => Yup.object().shape({
  header1: Yup.string().required('Header is required'),
  btn_content1: Yup.string().required('Button content is required'),
  header2: Yup.string().required('Header is required'),
  btn_content2: Yup.string().required('Button content is required'),
  header3: Yup.string().required('Header is required'),
  btn_content3: Yup.string().required('Button content is required'),
  image1: isEdit ? Yup.mixed() : Yup.mixed().required('Image is required'),
  image2: isEdit ? Yup.mixed() : Yup.mixed().required('Image is required'),
  image3: isEdit ? Yup.mixed() : Yup.mixed().required('Image is required'),
});

export default function StepCardsEdit() {
  const {id, partnerId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [wordLimitExceeded, setWordLimitExceeded] = useState(false);
  const [wordLimitExceeded2, setWordLimitExceeded2] = useState(false);
  const [wordLimitExceeded3, setWordLimitExceeded3] = useState(false);

  const [stepCards, setStepCards] = useState({
    header1: "",
    image1: "",
    btn_content1: "",
    header2: "",
    image2: "",
    btn_content2: "",
    header3: "",
    image3: "",
    btn_content3: "",
  });
  const inputRef1 = useRef(null);
  const inputRef2 = useRef(null);
  const inputRef3 = useRef(null);

  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();

  const fetchStepCardsData = async () => {
    setIsLoading(true);
    if (!Number(id)) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await getAboutUsStepCards(id);
      console.log(res);
      const stepData = res.data.data[0];
      setStepCards({
        ...stepData,
        index: res.data.data.indexOf(stepData),
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStepCardsData();
  }, [id]);

  const handleSubmit = async (values, { setSubmitting }) => {
    if (wordLimitExceeded || wordLimitExceeded2 || wordLimitExceeded3) {
      setSubmitting(false);
      return;
    }
    
    setIsUpdating(true);
    const formData = new FormData();

    // Update stepCards with new values before API call to prevent flickering
    const newStepCards = { ...stepCards };
    
    if (values.image1 instanceof File) {
      formData.append("image1", values.image1);
      newStepCards.image1 = URL.createObjectURL(values.image1);
    }
    formData.append("header1", values.header1);
    formData.append("btn_content1", values.btn_content1);
    
    if (values.image2 instanceof File) {
      formData.append("image2", values.image2);
      newStepCards.image2 = URL.createObjectURL(values.image2);
    }
    formData.append("header2", values.header2);
    formData.append("btn_content2", values.btn_content2);
    
    if (values.image3 instanceof File) {
      formData.append("image3", values.image3);
      newStepCards.image3 = URL.createObjectURL(values.image3);
    }
    formData.append("header3", values.header3);
    formData.append("btn_content3", values.btn_content3);

    try {
      if (!Number(id)) {
        formData.append("partner_id", partnerId);
        const response = await createAboutUsStepCards(formData);
        if(response.status === 200){
          showToast({
            type: 'success',
            message: response?.data?.message || "Step cards section has been create successfully!",
          });
          const stepCardData = response.data.data;
          setStepCards(stepCardData);
          navigate(`/${partnerId}/aboutus/stepcards/edit/${stepCardData.id}`);
        } else {
          showToast({
            type: 'error',
            message: response?.data?.message || "Failed to create step cards section. Please try again.",
          });
        }
      } else {
        const response = await updateAboutUsStepCards(id, formData);
        if(response.status === 200){
          // Update stepCards with response data but preserve our temporary image URLs
          setStepCards(prev => ({
            ...prev,
            ...response.data.data,
            image1: newStepCards.image1 || prev.image1,
            image2: newStepCards.image2 || prev.image2,
            image3: newStepCards.image3 || prev.image3
          }));
          showToast({
            type: 'success',
            message: response?.data?.message || "Step cards section has been updated successfully!",
          });
        } else {
          showToast({
            type: 'error',
            message: response?.data?.message || "Failed to update step cards section. Please try again.",
          });
        }
      }
    } catch (error) {
      showToast({
        type: 'error',
        message: error?.response?.data?.message || `Failed to ${Number(id) ? 'update' : 'create'} step cards section. Please try again.`,
      });
      console.error(error);
    } finally {
      setIsUpdating(false);
      setSubmitting(false);
    }
  };

  const handleBtnContentChange = (e, setFieldValue, field) => {
    const value = e.target.value;
    setFieldValue(field, value);
    
    const isExceeded = value.split(" ").length > 50 || value.length > 30;
    if (field === 'btn_content1') setWordLimitExceeded(isExceeded);
    else if (field === 'btn_content2') setWordLimitExceeded2(isExceeded);
    else if (field === 'btn_content3') setWordLimitExceeded3(isExceeded);
  };

  return (
    <>
    {isLoading ? <CmsCircularProgress/> : 
    <FormWrapper>
      <Formik
        initialValues={stepCards}
        validationSchema={() => validationSchema(Number(id))}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue
        }) => (
          <Form onSubmit={handleSubmit}>
            <Title>{Number(id) ? 'Edit' : 'Add' } Step Cards Section</Title>
            <SliderDiv>
              <SubTitle>{Number(id) ? 'Edit' : 'Add' } Cards</SubTitle>
              {/* Card 01 */}
              <Row>
                <Col md={4}>
                  <FormGroup 
                    requiredField
                    controlId="StepCardsControl" 
                    label='Card 01'
                    error={touched.image1 && errors.image1}
                  >
                    <Row>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          setFieldValue('image1', e.target.files[0]);
                        }}
                        style={{ display: "none" }}
                        ref={inputRef1}
                      />
                      {values.image1 instanceof File ? (
                        <ImageWrapper>
                          <UploadImage>
                            <BootstrapImage
                              src={URL.createObjectURL(values.image1)}
                              alt="image1"
                              onClick={() => inputRef1.current.click()}
                            />
                          </UploadImage>
                          <br />
                          <RemoveButtonWrapper>
                            <BootstrapButton 
                              onClick={() => {
                                  setFieldValue('image1', stepCards.image1);
                                  inputRef1.current.value = '';
                              }}
                            >
                              <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                            </BootstrapButton>
                          </RemoveButtonWrapper>
                        </ImageWrapper>
                      ) : stepCards.image1 ? (
                        <BootstrapImage
                          src={stepCards.image1}
                          alt="image1"
                          onClick={() => inputRef1.current.click()}
                          onMouseEnter={(e) => e.target.style.opacity = 0.8}
                          onMouseLeave={(e) => e.target.style.opacity = 1}
                        />
                      ) : (
                        <Button onClick={() => inputRef1.current.click()} className="mb-0">
                          Upload the image
                        </Button>
                      )}
                    </Row>
                  </FormGroup>
                </Col>
                <Col md={8} style={{ marginTop: "2.5vw" }}>
                  <FormGroup 
                    requiredField
                    label="Card Header" 
                    error={touched.header1 && errors.header1}
                  >
                    <Form.Control
                      type="input"
                      name="header1"
                      value={values.header1}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.header1 && errors.header1}
                    />
                  </FormGroup>

                  <FormGroup 
                    requiredField
                    label="Button Content" 
                    error={(touched.btn_content1 && errors.btn_content1) || (wordLimitExceeded ? 'Word limit exceeded.' : '')}
                  >
                    <Form.Control
                      type="input"
                      name="btn_content1"
                      value={values.btn_content1}
                      onChange={(e) => handleBtnContentChange(e, setFieldValue, 'btn_content1')}
                      onBlur={handleBlur}
                      isInvalid={(touched.btn_content1 && errors.btn_content1) || wordLimitExceeded}
                    />
                  </FormGroup>
                </Col>
              </Row>
              {/* Card 02 */}
              <Row>
                <Col md={4}>
                  <FormGroup 
                    requiredField
                    controlId="StepCardsControl" 
                    label='Card 02'
                    error={touched.image2 && errors.image2}
                  >
                    <Row>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          setFieldValue('image2', e.target.files[0]);
                        }}
                        style={{ display: "none" }}
                        ref={inputRef2}
                      />
                      {values.image2 instanceof File ? (
                        <ImageWrapper>
                          <UploadImage>
                            <BootstrapImage
                              src={URL.createObjectURL(values.image2)}
                              alt="image2"
                              onClick={() => inputRef2.current.click()}
                            />
                          </UploadImage>
                          <br />
                          <RemoveButtonWrapper>
                            <BootstrapButton 
                              onClick={() => {
                                setFieldValue('image2', stepCards.image2);
                                inputRef2.current.value = '';
                              }}
                            >
                              <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                            </BootstrapButton>
                          </RemoveButtonWrapper>
                        </ImageWrapper>
                      ) : stepCards.image2 ? (
                        <BootstrapImage
                          src={stepCards.image2}
                          alt="image2"
                          onClick={() => inputRef2.current.click()}
                          onMouseEnter={(e) => e.target.style.opacity = 0.8}
                          onMouseLeave={(e) => e.target.style.opacity = 1}
                        />
                      ) : (
                        <Button onClick={() => inputRef2.current.click()} className="mb-0">
                          Upload the image
                        </Button>
                      )}
                    </Row>
                  </FormGroup>
                </Col>
                <Col md={8} style={{ marginTop: "2.5vw" }}>
                  <FormGroup 
                    requiredField
                    label="Card Header"
                    error={touched.header2 && errors.header2}
                  >
                    <Form.Control
                      type="input"
                      name="header2"
                      value={values.header2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.header2 && errors.header2}
                    />
                  </FormGroup>

                  <FormGroup 
                    requiredField
                    label="Button Content"
                    error={(touched.btn_content2 && errors.btn_content2) || (wordLimitExceeded2 ? 'Word limit exceeded.' : '')}
                  >
                    <Form.Control
                      type="input"
                      name="btn_content2"
                      value={values.btn_content2}
                      onChange={(e) => handleBtnContentChange(e, setFieldValue, 'btn_content2')}
                      onBlur={handleBlur}
                      isInvalid={(touched.btn_content2 && errors.btn_content2) || wordLimitExceeded2}
                    />
                  </FormGroup>
                </Col>
              </Row>
              {/* Card 03 */}
              <Row>
                <Col md={4}>
                  <FormGroup 
                    requiredField
                    controlId="StepCardsControl" 
                    label='Card 03'
                    error={touched.image3 && errors.image3}
                  >
                    <Row>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          setFieldValue('image3', e.target.files[0]);
                        }}
                        style={{ display: "none" }}
                        ref={inputRef3}
                      />
                      {values.image3 instanceof File ? (
                        <ImageWrapper>
                          <UploadImage>
                            <BootstrapImage
                              src={URL.createObjectURL(values.image3)}
                              alt="image3"
                              onClick={() => inputRef3.current.click()}
                            />
                          </UploadImage>
                          <br />
                          <RemoveButtonWrapper>
                            <BootstrapButton 
                              onClick={() => {
                                setFieldValue('image3', stepCards.image3);
                                inputRef3.current.value = '';
                              }}
                            >
                              <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                            </BootstrapButton>
                          </RemoveButtonWrapper>
                        </ImageWrapper>
                      ) : stepCards.image3 ? (
                        <BootstrapImage
                          src={stepCards.image3}
                          alt="image3"
                          onClick={() => inputRef3.current.click()}
                          onMouseEnter={(e) => e.target.style.opacity = 0.8}
                          onMouseLeave={(e) => e.target.style.opacity = 1}
                        />
                      ) : (
                        <Button onClick={() => inputRef3.current.click()} className="mb-0">
                          Upload the image
                        </Button>
                      )}
                    </Row>
                  </FormGroup>
                </Col>
                <Col md={8} style={{ marginTop: "2.5vw" }}>
                  <FormGroup 
                    requiredField
                    label="Card Header"
                    error={touched.header3 && errors.header3}
                  >
                    <Form.Control
                      type="input"
                      name="header3"
                      value={values.header3}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.header3 && errors.header3}
                    />
                  </FormGroup>

                  <FormGroup 
                    requiredField
                    label="Button Content"
                    error={(touched.btn_content3 && errors.btn_content3) || (wordLimitExceeded3 ? 'Word limit exceeded.' : '')}
                  >
                    <Form.Control
                      type="input"
                      name="btn_content3"
                      value={values.btn_content3}
                      onChange={(e) => handleBtnContentChange(e, setFieldValue, 'btn_content3')}
                      onBlur={handleBlur}
                      isInvalid={(touched.btn_content3 && errors.btn_content3) || wordLimitExceeded3}
                    />
                  </FormGroup>
                </Col>
              </Row>
              {/* Button section */}
              <ButtonWrapper className="mt-3">
                <Link to={`/${partnerId}/aboutus/stepcards/view/${Number(id) ? id : 'create'}`}>
                  <Button variant="secondary">Go Back</Button>
                </Link>
                <div>
                  <Button
                    type="submit"
                    disabled={isSubmitting || wordLimitExceeded || wordLimitExceeded2 || wordLimitExceeded3}
                  >
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </ButtonWrapper>
            </SliderDiv>
          </Form>
        )}
      </Formik>
    </FormWrapper>}
    </>
  );
}

const SliderDiv = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const FormWrapper = styled.div`
   @media (min-width:576px) {
   padding: 1rem;
  }
    @media (min-width:768px) {
   padding: 1.5rem;
  }
   padding: .5rem;
`;

const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;

const SubTitle = styled.h6``;

const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  gap: 2rem;
 @media (max-width: 425px) {
  gap: 1rem;
}
`;
const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  width: 13vw;
  position: absolute;
    top: -17px;
    right: -14px;
    @media (max-width:576px) {
     top: -9px;
    right: -17px;
`;
const BootstrapImage = styled('img')(({ theme }) => ({
  maxWidth: "13vw",
  cursor: "pointer",
  "@media (max-width: 576px)": {
    maxWidth: "100%",
  },
}));
const UploadImage = styled.div`
  max-width: 13vw;
   overflow: hidden;
  border-radius: 6px;
  @media (max-width:576px) {
   max-width: 80vw;
  }
`;
const ImageWrapper = styled.div`
    position:relative;
    max-width: 13vw;
   @media (max-width:576px) {
   max-width: 80vw;
  }
`;
const  BootstrapButton = styled('button')(({ theme }) => ({
  margin: '0 !important',
  backgroundColor:'#FFFFFF',
  padding: '5px 7px',
  border: '1px solid #dc3545',
  width:'auto',
  "&:hover": {
    background: '#fffeff',
    boxShadow:'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px'
  },
  '@media (max-width:576px)': {
    padding: '2px 4px',
  },
}));